import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Linkedin, Mail, X } from "lucide-react";
import { api } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import Avatar from "@/components/site/Avatar";

export default function About() {
  const [team, setTeam] = useState([]);
  const [activeMember, setActiveMember] = useState(null);
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)); }, []);

  // Lock body scroll while modal is open + close on Esc
  useEffect(() => {
    if (!activeMember) return;
    const onKey = (e) => e.key === "Escape" && setActiveMember(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [activeMember]);

  return (
    <PageTransition>
      <Seo title="About" description="The team behind Pulse Media — reshaping the entertainment industry using talent." />
      <section className="pt-40 pb-16 px-6 lg:px-10" data-testid="about-hero">
        <div className="max-w-5xl mx-auto">
          <div className="label-mono mb-4">/ About</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[1.0]">
            Reshaping the entertainment industry using <span className="gradient-text">talent.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-300 max-w-3xl leading-relaxed">
            Pulse Media is a four-branch record label and media company at the intersection of entertainment and technology.
            We pioneer new ways to experience music, movies, and technology — producing films, records, and debuting talent across different realms of entertainment.
          </p>
        </div>
      </section>

      <Section className="px-6 lg:px-10 py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            { label: "Music", value: "Distribution + Label", desc: "80+ DSPs across 230+ countries via Empire Distribution & Virgin Music Group." },
            { label: "Film", value: "Original Productions", desc: "Reality shows, scripted series, feature films, and documentaries." },
            { label: "Technology", value: "Pulse Portal", desc: "A streaming platform built for creatives and the audiences who love them." },
          ].map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="p-8 rounded-2xl border border-white/5 bg-[#121215] lift"
            >
              <div className="label-mono mb-3 text-cyan-400">/ {b.label}</div>
              <div className="font-display text-2xl font-semibold tracking-tight mb-3">{b.value}</div>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section className="px-6 lg:px-10 py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="label-mono mb-3">/ The Team</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">The people behind it.</h2>
            <p className="mt-3 text-zinc-400 max-w-xl">Tap any team member to read their bio.</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {team.map((m, i) => (
              <motion.button
                type="button"
                key={m.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setActiveMember(m)}
                className="team-card border border-white/5 bg-[#121215] cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-cyan-400/60"
                style={{ aspectRatio: "3/4" }}
                data-testid={`team-${m.name.toLowerCase().replace(/\s/g,'-')}`}
                aria-label={`Open profile for ${m.name}`}
              >
                <Avatar src={m.image} name={m.name} className="absolute inset-0 w-full h-full pointer-events-none" />
                {/* always-visible nameplate */}
                <div className="absolute top-3 left-3 right-3 z-10">
                  <div className="font-display text-base font-bold tracking-tight leading-tight">{m.name}</div>
                  <div className="label-mono text-cyan-300/90 mt-0.5" style={{ fontSize: 9 }}>{m.role}</div>
                </div>
                {/* hover preview overlay (desktop only — mobile gets the modal) */}
                <div className="team-overlay">
                  <p className="text-xs text-zinc-200 leading-relaxed mb-3 line-clamp-6">{m.bio}</p>
                  <div className="flex gap-2">
                    {m.instagram && <a onClick={(e) => e.stopPropagation()} href={m.instagram} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2" aria-label="Instagram"><Instagram size={12} /></a>}
                    {m.linkedin && <a onClick={(e) => e.stopPropagation()} href={m.linkedin} target="_blank" rel="noopener noreferrer" className="glass-btn ghost p-2" aria-label="LinkedIn"><Linkedin size={12} /></a>}
                    {m.email && <a onClick={(e) => e.stopPropagation()} href={`mailto:${m.email}`} className="glass-btn ghost p-2" aria-label="Email"><Mail size={12} /></a>}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </Section>

      {/* Member detail modal */}
      <AnimatePresence>
        {activeMember && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(16px)" }}
            onClick={() => setActiveMember(null)}
            data-testid="team-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0a0a0c]"
              style={{ boxShadow: "0 30px 80px rgba(0,184,255,0.12)" }}
            >
              <button
                onClick={() => setActiveMember(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
                aria-label="Close"
                data-testid="team-modal-close"
              >
                <X size={16} />
              </button>

              <div className="grid md:grid-cols-[280px_1fr] gap-0">
                {/* Photo */}
                <div className="relative bg-[#121215] md:rounded-l-3xl overflow-hidden">
                  <div className="aspect-[3/4] md:aspect-auto md:h-full">
                    <Avatar src={activeMember.image} name={activeMember.name} className="w-full h-full" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:via-transparent md:to-black/30 pointer-events-none" />
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 md:p-8 lg:p-10">
                  <div className="label-mono text-cyan-300 mb-3">{activeMember.role}</div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tighter leading-[1.0]" data-testid="team-modal-name">
                    {activeMember.name}
                  </h3>

                  {activeMember.bio && (
                    <div className="mt-6 space-y-4 text-zinc-300 leading-relaxed text-sm md:text-base whitespace-pre-line" data-testid="team-modal-bio">
                      {activeMember.bio}
                    </div>
                  )}

                  {(activeMember.instagram || activeMember.linkedin || activeMember.email) && (
                    <div className="mt-8 pt-6 border-t border-white/5">
                      <div className="label-mono text-zinc-500 mb-3">/ Connect</div>
                      <div className="flex flex-wrap gap-2">
                        {activeMember.instagram && (
                          <a href={activeMember.instagram} target="_blank" rel="noopener noreferrer" className="glass-btn ghost" data-testid="team-modal-instagram">
                            <Instagram size={14} /> Instagram
                          </a>
                        )}
                        {activeMember.linkedin && (
                          <a href={activeMember.linkedin} target="_blank" rel="noopener noreferrer" className="glass-btn ghost" data-testid="team-modal-linkedin">
                            <Linkedin size={14} /> LinkedIn
                          </a>
                        )}
                        {activeMember.email && (
                          <a href={`mailto:${activeMember.email}`} className="glass-btn ghost" data-testid="team-modal-email">
                            <Mail size={14} /> Email
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageTransition>
  );
}
